import type { SchemaObject } from 'json-schema-traverse';
import traverse from 'json-schema-traverse';

type ConditionalPath = {
  path: string;
  condition: SchemaObject;
  type: 'then' | 'else';
};

/**
 * Extracts all paths from a JSON schema that are under conditions (if/then/else)
 * @param schema The JSON schema to analyze
 * @returns Array of objects containing the path and its associated condition
 */
function getConditionalNodes(schema: SchemaObject): ConditionalPath[] {
  const conditionalPaths: ConditionalPath[] = [];

  traverse(schema, {
    cb: (currSchema, path) => {
      // Skip if no condition
      if (!currSchema.if) return;

      // Get the current path in dot notation
      const dotPath = path
        .split('/')
        .filter(Boolean)
        .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'))
        .join('.');

      // If there's a then clause, add its properties
      if (currSchema.then?.properties) {
        const traverseProperties = (properties: Record<string, any>, basePath: string) => {
          Object.entries(properties).forEach(([prop, schema]) => {
            const currentPath = basePath ? `${basePath}.${prop}` : prop;

            conditionalPaths.push({
              path: currentPath,
              condition: currSchema.if,
              type: 'then',
            });

            if (schema.properties) {
              traverseProperties(schema.properties, currentPath);
            }
          });
        };

        traverseProperties(currSchema.then.properties, dotPath);
      }
    },
  });

  return conditionalPaths;
}

export function getConditionalPaths(schema: SchemaObject): string[] {
  const paths = getConditionalNodes(schema)
    .map((node) => stripSchemaSpecificPartsFromPath(node.path))
    .filter(Boolean);

  return [...new Set(paths)].sort();
}

export function stripSchemaSpecificPartsFromPath(path: string) {
  return (
    path
      // Remove SubmissionForm properties prefix
      .replace(/^properties\.SubmissionForm\.properties\.?/, '')
      .replace(/^properties\.SubmissionForm\.?/, '')
      // Remove allOf segments with their indices
      .replace(/\.allOf\.\d+\.?/g, '.')
      .replace(/^allOf\.\d+\.risk_details/, 'risk_details')
      .replace(/^risk_details$/, '')
      // Remove items segments with their indices
      .replace(/\.items\.\d+\.?/g, '.0')
      .replace(/\.items\./g, '.0.')
      // Remove properties segments
      .replace(/\.properties\./g, '.')
      // Remove then segments
      .replace(/\.then\./g, '.')
      // Remove any trailing dots
      .replace(/\.$/, '')
      // Remove any double dots that might have been created
      .replace(/\.+/g, '.')
  );
}
